import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MemberScope, MemberService } from '../../core/services/member.service';
import { PaymentService } from '../../core/services/payment.service';
import { TenantContextService } from '../../core/services/tenant-context.service';
import { AuthService } from '../../core/services/auth.service';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';
import { NoBuSelectedComponent } from '../../shared/components/no-bu-selected/no-bu-selected.component';
import { AuditLogService, AuditLogEntry } from '../../core/services/audit-log.service';
import { SubscriptionUsageCardComponent } from '../billing/subscription-usage-card.component';
import { SubscriptionStatusBannerComponent } from '../billing/subscription-status-banner.component';
import { MemberDuesAlertComponent } from '../../shared/components/member-dues-alert/member-dues-alert.component';
import { EventDuesAlertComponent } from '../../shared/components/event-dues-alert/event-dues-alert.component';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterLink, HasPermissionDirective, NoBuSelectedComponent, SubscriptionUsageCardComponent, SubscriptionStatusBannerComponent, MemberDuesAlertComponent, EventDuesAlertComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent {
  private memberService = inject(MemberService);
  private paymentService = inject(PaymentService);
  private tenantContext = inject(TenantContextService);
  private auth = inject(AuthService);
  private auditLog = inject(AuditLogService);
  private router = inject(Router);

  readonly hasBuSelected = computed(() => this.tenantContext.selectedBusinessUnitId() != null);
  readonly isAdmin = computed(() => this.auth.isSuperAdminSignal() || this.auth.isTenantAdminSignal());
  readonly canSeeStats = computed(() => this.isAdmin() || this.hasBuSelected());

  readonly userName = computed(() => this.auth.user()?.fullName || this.auth.user()?.username || '');

  totalMembers = 0;
  totalRevenue = 0;
  thisMonthPayments = 0;

  isLoading = false;
  currentMonth = new Date().getMonth() + 1;
  currentYear = new Date().getFullYear();

  readonly recentActivity = signal<AuditLogEntry[]>([]);
  readonly isLoadingActivity = signal(false);

  constructor() {
    effect(() => {
      this.tenantContext.selectedBusinessUnitId();
      this.loadDashboardStats();
      this.loadRecentActivity();
    });
  }

  private computeScope(): MemberScope {
    return this.isAdmin() ? 'all' : 'self';
  }

  loadDashboardStats(): void {
    if (!this.canSeeStats()) {
      this.totalMembers = 0;
      this.totalRevenue = 0;
      this.thisMonthPayments = 0;
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    if (this.isAdmin()) {
      this.memberService.getMembers({ scope: this.computeScope(), page: 1, pageSize: 1 }).subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.totalMembers = response.data.totalCount ?? 0;
          }
          this.checkLoadingComplete();
        },
        error: () => this.checkLoadingComplete()
      });
    }

    this.paymentService.getPayments({ page: 1, pageSize: 1000, month: this.currentMonth, year: this.currentYear }).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          let payments: any[] = [];
          if (Array.isArray(response.data)) {
            payments = response.data;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            payments = response.data.data;
          }
          this.thisMonthPayments = payments.length;
          this.totalRevenue = payments.reduce((sum, p) => sum + (p.amount || p.Amount || 0), 0);
        }
        this.checkLoadingComplete();
      },
      error: () => this.checkLoadingComplete()
    });
  }

  loadRecentActivity(): void {
    this.isLoadingActivity.set(true);
    const scope = this.isAdmin() ? 'all' : 'self';
    this.auditLog.getRecent(scope, 8).subscribe({
      next: (res) => {
        this.isLoadingActivity.set(false);
        this.recentActivity.set(res?.success && res.data ? res.data : []);
      },
      error: () => {
        this.isLoadingActivity.set(false);
        this.recentActivity.set([]);
      }
    });
  }

  private checkLoadingComplete(): void {
    this.isLoading = false;
  }

  goToActivity(entry: AuditLogEntry): void {
    if (!entry.entityType || !entry.entityId) return;
    const t = entry.entityType.toLowerCase();
    const routes: Record<string, string> = {
      payment: '/payments',
      member: '/members',
      event: '/events',
      user: '/account/profile',
      tenant: '/admin/tenants',
      businessunit: '/admin/business-units'
    };
    const base = routes[t];
    if (!base) return;
    if (t === 'user' || t === 'businessunit') {
      this.router.navigateByUrl(base);
    } else {
      this.router.navigate([base, entry.entityId]);
    }
  }

  verbFor(action: string): string {
    const map: Record<string, string> = {
      Create: 'created',
      Update: 'updated',
      Delete: 'deleted',
      Restored: 'restored',
      UserAccountReactivated: 'reactivated user account for',
      Login: 'logged in',
      Logout: 'logged out',
      LoginFailed: 'failed login',
      PasswordChanged: 'changed password',
      PasswordReset: 'reset password',
      ReceiptSent: 'sent receipt for',
      LedgerAdjusted: 'adjusted ledger for',
      LedgerReversed: 'reversed ledger entry for',
      RoleGranted: 'granted role on',
      RoleRevoked: 'revoked role from',
      BuAssigned: 'assigned BU to',
      BuRemoved: 'removed BU from',
      InvitationSent: 'invited',
      InvitationAccepted: 'accepted invitation for',
      ChangeRequestSubmitted: 'submitted change request for',
      ChangeRequestApproved: 'approved change request for',
      ChangeRequestRejected: 'rejected change request for',
      ChangeRequestCancelled: 'cancelled change request for',
      EventClosed: 'closed event',
      EventFundedFromCommittee: 'funded event from committee',
      EventCloseRequested: 'requested close of',
      EventCloseApproved: 'approved close of',
      OcrExtracted: 'extracted OCR text from',
      OcrFailed: 'attempted OCR extraction on',
      SubscriptionStarted: 'started subscription on',
      SubscriptionTrialStarted: 'started trial on',
      SubscriptionPlanChanged: 'changed plan on',
      SubscriptionCancelled: 'cancelled subscription on',
      SubscriptionResumed: 'resumed subscription on',
      SubscriptionRenewed: 'renewed subscription on',
      SubscriptionExpired: 'expired subscription on',
      SubscriptionExtended: 'extended subscription on',
      SubscriptionPlanActivated: 'activated plan',
      SubscriptionPlanDeactivated: 'deactivated plan',
      InvoiceIssued: 'issued invoice for',
      InvoicePaid: 'recorded payment for invoice',
      InvoiceVoided: 'voided invoice',
      PaymentSubmissionSubmitted: 'submitted payment proof for',
      PaymentSubmissionApproved: 'approved payment submission for',
      PaymentSubmissionRejected: 'rejected payment submission for',
      ExemptionRequested: 'requested exemption for',
      ExemptionApproved: 'approved exemption for',
      ExemptionRejected: 'rejected exemption for',
      EntitlementBlocked: 'blocked by entitlement on'
    };
    return map[action] || action;
  }

  formatRelative(ts: string): string {
    if (!ts) return '';
    const diffMs = Date.now() - new Date(ts).getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day}d ago`;
    return `${Math.floor(day / 7)}w ago`;
  }

  initials(name?: string | null): string {
    if (!name) return 'SYS';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
}
